import { T, Var, useGT } from 'gt-react';
import type { CashReceiptDetail, CashReceiptSettlement } from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { formatAmount } from './cashReceiptFormat';

type Translate = ReturnType<typeof useGT>;

function settlementLabel(state: string, gt: Translate): string {
  switch (state) {
    case 'settled':
      return gt('Settled', {
        context:
          "AR cash receipt settlement badge: linked remittances explain all of the receipt's cash",
      });
    case 'under':
      return gt('Under', {
        context:
          "AR cash receipt settlement badge: linked remittances explain less than the receipt's cash",
      });
    case 'over':
      return gt('Over', {
        context:
          'AR cash receipt settlement badge: linked remittances state more than the receipt amount, likely a duplicate',
      });
    default:
      return gt('Indeterminate', {
        context:
          'AR cash receipt settlement badge: the receipt or a linked remittance has no amount to compare',
      });
  }
}

function settlementClass(state: string): string {
  switch (state) {
    case 'settled':
      return 'bg-green-50 text-green-800 border-green-200';
    case 'under':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'over':
      return 'bg-red-50 text-red-800 border-red-200';
    default:
      return 'text-muted-foreground';
  }
}

/**
 * Whether the remittances linked to the receipt explain all of its cash:
 * their stated totals against the receipt amount, to the cent. Derived on the
 * server from the receipt's active links (GEN-3091), never stored.
 */
export function CashReceiptSettlementBadge({
  settlement,
}: {
  settlement: CashReceiptSettlement;
}) {
  const gt = useGT();
  return (
    <Badge
      variant="outline"
      className={`shrink-0 ${settlementClass(settlement.state)}`}
      data-testid="cash-receipt-settlement-badge"
    >
      {settlementLabel(settlement.state, gt)}
    </Badge>
  );
}

/** "Linked remittances: $X of $Y (N)", the numbers behind the badge. */
export function CashReceiptSettlementSummary({
  receipt,
}: {
  receipt: Pick<CashReceiptDetail, 'settlement' | 'paymentAmount'>;
}) {
  const { settlement } = receipt;
  return (
    <p
      className="text-xs text-muted-foreground"
      data-testid="cash-receipt-settlement-summary"
    >
      <T context="AR cash receipt: how much of the receipt amount its linked remittances explain, and how many remittances are linked">
        Linked remittances: <Var>{formatAmount(settlement.linkedTotal)}</Var> of{' '}
        <Var>{formatAmount(receipt.paymentAmount)}</Var> (
        <Var>{settlement.activeLinkCount}</Var>)
      </T>
    </p>
  );
}
